/**
 * AuthorizationsScreen — Authorizations & Referrals
 *
 * v217.0.0 (WRP-6): Two-section model with 3-class uploader tagging
 *   - "For this visit"   — pre-visit support documents (auths, referrals from
 *                          prior providers). Either the PATIENT or the MD can
 *                          add these.
 *   - "From this visit"  — Orella-generated artifacts from this encounter
 *                          (signed prior auth packets, referral letters
 *                          Orella delivered). Read-only.
 *
 * Each entry on case.adminDocuments.{authorization,referral} carries
 * `uploadedBy: 'patient' | 'md' | 'orella'` (canonical) and
 * `uploadedByName` (e.g. 'Dr. Kim') for display.
 *
 * Back-compat reads:
 *   - legacy `source: 'patient'`      → uploadedBy: 'patient'
 *   - legacy `source: 'md_generated'` → uploadedBy: 'orella'
 *     (the legacy field was only used for system-generated artifacts)
 *
 * v214.7.4 also fixes the off-brand purple color on the referral docs
 * (was #7C3AED) → navy/blue per the brand palette.
 */

import { useMemo, useState, useRef } from 'react'
import { currentEncounterNumber } from '../../utils/caseEncounters'
import { classifyAuthUpload } from '../../utils/authDocClass'
import { cleanDiagnosisText } from '../../utils/diagnosisText'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, FileCheck, FileText, Eye, Calendar, Building2, User, Phone, Upload, Check, AlertTriangle, Sparkles, Plus, Hash, Printer, ShieldCheck, ClipboardList, Stethoscope } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { formatDate } from '../../utils/dateUtils'
import { priorAuthPayerEntries, PAYER_STATUS_KEYS } from '../../utils/priorAuthStatus'
import { casesAPI, aiAPI } from '../../services/api'
import { uploadToStorage } from '../../services/storage'
import BottomSheet from '../../components/BottomSheet'
import StoredDocViewer from '../../components/StoredDocViewer'
import OrellaThinkingBubble from '../../components/OrellaThinkingBubble'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// Brand colors (no purple)
const ORELLA_BLUE = '#46A8F9'
const NAVY = '#15354F'
const ICE_BLUE = '#E9F8FF'
const LIGHT_BLUE = '#A7D9FC'
const GREEN = '#16A34A'
const GREEN_LIGHT = '#DCFCE7'
const AMBER = '#B45309'
const AMBER_BG = '#FEF3C7'

// HMO detection — mirrors MD-side waitingRoomReadiness.isHMOPlan logic.
const HMO_PATTERNS = [/\bHMO\b/i, /\bEPO\b/i, /Health Plan/i, /Medicare Advantage HMO/i, /Medicaid HMO/i]
const PPO_PATTERNS = [/\bPPO\b/i, /Original Medicare/i, /Self.?pay/i]

function isHMOInsurance(insurance) {
  if (!insurance) return false
  // Patient-side ProfileInsurance writes coverageType: 'hmo' as the canonical
  // structured field. Plan name string is a fallback for older records.
  if ((insurance.coverageType || '').toLowerCase() === 'hmo') return true
  if ((insurance.planType || '').toLowerCase() === 'hmo') return true
  const planString = `${insurance.provider || insurance.insurerName || ''} ${insurance.plan || insurance.planType || ''}`.trim()
  if (!planString) return false
  for (const pat of PPO_PATTERNS) if (pat.test(planString)) return false
  for (const pat of HMO_PATTERNS) if (pat.test(planString)) return true
  return false
}

/**
 * DocumentCard - Card for displaying a single document
 */
function DocumentCard({ document: doc, type, onClick, color = GREEN, lightColor = GREEN_LIGHT }) {
  const config = doc.config || {}

  const title = doc.procedureName ||
    doc.serviceName ||
    config.serviceType ||
    config.procedures?.[0]?.description ||
    config.procedures?.[0]?.code ||
    doc.specialty ||
    doc.providerName ||
    doc.name ||
    (type === 'authorization' ? 'Prior Authorization' : 'Referral Letter')

  const subtitle = doc.facilityName ||
    config.facilityDetails?.name ||
    doc.insuranceInfo?.name ||
    config.insurance?.provider ||
    doc.reasonForReferral ||
    doc.referringProviderName ||
    ''

  const date = doc.createdAt || doc.signedAt || doc.uploadedAt || doc.date
  const status = doc.status || (doc.signedAt ? 'signed' : doc.url ? 'on file' : 'pending')

  // v217.0.0: Uploader attribution. Reads canonical uploadedBy + uploadedByName,
  // falls back to legacy `source` field. See getEntryDestination() for the
  // full back-compat shape.
  const uploadedBy = doc.uploadedBy
    || (doc.source === 'patient' ? 'patient' : null)
    || (doc.source === 'md_generated' ? 'orella' : null)
  let uploaderLabel = null
  let uploaderColor = '#94A3B8'
  if (uploadedBy === 'patient') {
    uploaderLabel = 'Uploaded by you'
    uploaderColor = ORELLA_BLUE
  } else if (uploadedBy === 'md') {
    uploaderLabel = `Uploaded by ${doc.uploadedByName || 'your physician'}`
    uploaderColor = NAVY
  } else if (uploadedBy === 'orella') {
    uploaderLabel = 'Uploaded by Orella'
    uploaderColor = GREEN
  }

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-xl p-4 border border-slate-200 hover:border-slate-300 transition-all text-left"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: lightColor }}
        >
          {type === 'authorization' ? (
            <FileCheck size={20} style={{ color }} />
          ) : (
            <FileText size={20} style={{ color }} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm mb-0.5" style={{ color: NAVY }}>
            {title}
          </h4>
          {subtitle && (
            <p className="text-xs text-slate-500 mb-2 truncate">{subtitle}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-slate-400">
            {date && (
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {formatDate(date)}
              </span>
            )}
            {status && (
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                style={{
                  backgroundColor: status === 'approved' ? GREEN_LIGHT :
                    status === 'denied' ? '#FEE2E2' :
                    status === 'on file' ? ICE_BLUE : '#FEF3C7',
                  color: status === 'approved' ? GREEN :
                    status === 'denied' ? '#DC2626' :
                    status === 'on file' ? NAVY : '#D97706'
                }}
              >
                {status === 'approved' ? 'Approved' :
                 status === 'denied' ? 'Denied' :
                 status === 'signed' ? 'Submitted' :
                 status === 'on file' ? 'On file' : 'Pending'}
              </span>
            )}
          </div>
          {uploaderLabel && (
            <p className="text-[10px] mt-1.5" style={{ color: uploaderColor }}>
              {uploaderLabel}
            </p>
          )}
        </div>
        <Eye size={18} className="text-slate-400 flex-shrink-0" />
      </div>
    </button>
  )
}

// ★ TEST 2.22 (patient half, Dr. Rami R40), P3-2 — the payer's answer on a prior authorization
//   the physician generated: Submitted / Approved / Denied and the payer's reference number,
//   read from the case's caseRequests entry (payerStatus / payerReference, orella-api D6a). An
//   entry without those keys is not shown (today's view). Gate: check-authorization-status.mjs.
const PAYER_PILL = {
  submitted: { color: ORELLA_BLUE, background: ICE_BLUE },
  approved: { color: GREEN, background: GREEN_LIGHT },
  denied: { color: '#B91C1C', background: '#FEF2F2' },
}

function PayerStatusCard({ entry }) {
  const { t } = useI18n()
  const pill = PAYER_PILL[entry.payerStatus]
  return (
    <div className="w-full bg-white rounded-xl p-4 border border-slate-200" data-payer-status={entry.payerStatus}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: GREEN_LIGHT }}>
          <ShieldCheck size={20} style={{ color: GREEN }} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-sm mb-1" style={{ color: NAVY }}>{t('authDocs.payerTitle')}</h4>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={pill}>{t(PAYER_STATUS_KEYS[entry.payerStatus])}</span>
            {entry.payerStatusAt && <span className="text-xs text-slate-400">{formatDate(entry.payerStatusAt)}</span>}
          </div>
          {entry.payerReference && (
            <p className="text-xs text-slate-600 mt-1.5">{t('authDocs.payerReference', { reference: entry.payerReference })}</p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * DocumentDetailModal - Modal for viewing document details
 */
function DocumentDetailModal({ document: doc, onClose, caseId }) {
  const { t } = useI18n()
  const [viewing, setViewing] = useState(false)
  if (!doc) return null

  const isAuthorization = doc.type === 'authorization'
  const color = isAuthorization ? GREEN : ORELLA_BLUE
  const lightColor = isAuthorization ? GREEN_LIGHT : ICE_BLUE

  const config = doc.config || {}
  const procedureName = doc.procedureName ||
    doc.serviceName ||
    config.serviceType ||
    config.procedures?.[0]?.description ||
    doc.specialty ||
    doc.providerName ||
    doc.name

  const procedures = config.procedures || []
  const cptCodes = procedures.map(p => p.code).filter(Boolean)

  const facilityName = doc.facilityName || config.facilityDetails?.name
  const facilityAddress = doc.facilityAddress || config.facilityDetails?.address
  const facilityPhone = doc.facilityPhone || config.facilityDetails?.phone

  const insuranceInfo = doc.insuranceInfo || config.insurance || {}
  const insuranceName = insuranceInfo.name || insuranceInfo.provider || ''
  const memberId = insuranceInfo.memberId || ''

  // R14 / PROD-260 (patient P1): the diagnosis text, never a raw ICD-10 code. The "ICD-10: …"
  // line under it is gone (utils/diagnosisText.js; the API strip follows in D5c).
  const diagnosis = cleanDiagnosisText(doc.diagnosis || doc.reasonForReferral || doc.clinicalIndication ||
    config.diagnoses?.[0]?.description || '')

  const status = doc.status || (doc.signedAt ? 'signed' : doc.url ? 'on file' : 'pending')
  const notes = doc.notes || config.notes || ''
  const createdAt = doc.createdAt || doc.signedAt || doc.uploadedAt
  const referringProvider = doc.referringProviderName

  return (
    // v214.9.3: Use generic BottomSheet for body-scroll-lock + iOS touch handling.
    // Previously: background page would scroll when user touched the modal — fixed
    // by BottomSheet's position:fixed body lock + overscroll-behavior:contain.
    <BottomSheet open={true} onClose={onClose} showHandle={false} maxHeight="85dvh">
        <div className="px-4 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: lightColor }}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center bg-white"
            >
              {isAuthorization ? <FileCheck size={20} style={{ color }} /> : <FileText size={20} style={{ color }} />}
            </div>
            <div>
              <h3 className="font-bold text-base" style={{ color: NAVY }}>
                {isAuthorization ? 'Authorization Details' : 'Referral Details'}
              </h3>
              {createdAt && (
                <p className="text-xs text-slate-500">{formatDate(createdAt)}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <span className="text-xl">×</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {procedureName && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {isAuthorization ? 'Procedure / Service' : 'Specialty / Clinician'}
              </p>
              <p className="text-sm font-medium" style={{ color: NAVY }}>{procedureName}</p>
              {cptCodes.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">CPT: {cptCodes.join(', ')}</p>
              )}
            </div>
          )}

          {referringProvider && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {t('authDocs.referringProvider', 'Referring Provider')}
              </p>
              <p className="text-sm" style={{ color: NAVY }}>{referringProvider}</p>
            </div>
          )}

          {diagnosis && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {t('authDocs.clinicalIndication', 'Clinical Indication')}
              </p>
              <p className="text-sm" style={{ color: NAVY }}>{diagnosis}</p>
            </div>
          )}

          {(facilityName || facilityAddress || facilityPhone) && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {t('authDocs.facility', 'Facility')}
              </p>
              {facilityName && (
                <p className="text-sm font-medium flex items-center gap-2" style={{ color: NAVY }}>
                  <Building2 size={14} className="text-slate-400" />{facilityName}
                </p>
              )}
              {facilityAddress && <p className="text-xs text-slate-500 ml-6">{facilityAddress}</p>}
              {facilityPhone && (
                <p className="text-xs text-slate-500 ml-6 flex items-center gap-1">
                  <Phone size={11} />{facilityPhone}
                </p>
              )}
            </div>
          )}

          {(insuranceName || memberId) && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {t('profile.insurance', 'Insurance')}
              </p>
              {insuranceName && <p className="text-sm" style={{ color: NAVY }}>{insuranceName}</p>}
              {memberId && <p className="text-xs text-slate-500">{t('deliver.memberIdLabel', 'Member ID:')} {memberId}</p>}
            </div>
          )}

          {notes && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{t('authDocs.notes', 'Notes')}</p>
              <p className="text-sm whitespace-pre-wrap" style={{ color: NAVY }}>{notes}</p>
            </div>
          )}

          {/* ★ s101 Phase 3 (x_19): an in-app viewer on a FRESH link, not a _blank anchor to a
              link signed an hour ago (components/StoredDocViewer.jsx). */}
          {(doc.url || doc.storagePath || doc.path) && (
            <button
              type="button"
              data-cta="auth-view-document"
              onClick={() => setViewing(true)}
              className="block w-full text-center text-sm font-semibold py-2.5 rounded-xl"
              style={{ background: ICE_BLUE, color: NAVY, border: `1.5px solid ${LIGHT_BLUE}` }}
            >
              {t('authDocs.viewDocument')}
            </button>
          )}
        </div>
      <StoredDocViewer open={viewing} doc={doc} caseId={caseId} title={procedureName} onClose={() => setViewing(false)} t={t} />
    </BottomSheet>
  )
}

/**
 * PatientUploadSection — v214.7.4 (WRP-5)
 *
 * The "For this visit" section. Patient-facing upload affordance for HMO
 * referral letters and prior auth documents the patient brings in. Three
 * inputs (per Dr. Rami's spec):
 *   1. File upload (PDF or image)
 *   2. Auth number (text)
 *   3. Optional referring-provider name (text)
 *
 * On submit:
 *   - File → Supabase Storage at case-files/<caseId>/, gets a public URL
 *   - Entry appended to case.adminDocuments.referral with source: 'patient'
 *   - If auth # provided: writes case.appointmentRequest.authNumber so the
 *     MD-side Waiting Room readiness pill picks it up immediately.
 *   - If referring-provider provided: tags the entry with that name, which
 *     the MD-side server-side projection will surface (when the MD hits
 *     "Process" on the doc, Sonnet's referringDoctor extract uses the
 *     uploaded PDF; the patient-typed name is a useful tie-breaker).
 *
 * Uploads from this component always tag source: 'patient' so the screen
 * sorts them into the "For this visit" section regardless of which API
 * column they land in.
 */
export function PatientUploadSection({ currentCase, isHMO, hasUploaded, onUploaded, bookingMode = false, onDefer, encounterNumber }) {
  const { t } = useI18n()
  const fileInputRef = useRef(null)
  const [stage, setStage] = useState('idle') // idle | analyzing | review | auth-confirm | auth-valid | reject | saving | saved | error
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState(isHMO && !hasUploaded)
  const [pendingFile, setPendingFile] = useState(null)
  const [detectedType, setDetectedType] = useState(null)
  const [extracted, setExtracted] = useState(null)
  const [authReject, setAuthReject] = useState(null)
  const [authFieldError, setAuthFieldError] = useState(null)

  const resetFlow = () => {
    setStage('idle'); setError(null)
    setPendingFile(null); setDetectedType(null); setExtracted(null)
    setAuthReject(null); setAuthFieldError(null)
  }

  const handleFilePick = async (e) => {
    const f = e.target.files?.[0]
    if (e.target) e.target.value = ''
    if (!f) return
    if (f.size > 20 * 1024 * 1024) { setError(t('authDocs.errTooLarge', 'File is too large — keep it under 20MB.')); return }
    const ok = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic', 'image/heif']
    if (!ok.includes(f.type) && !f.name.match(/\.(pdf|jpg|jpeg|png|heic|heif)$/i)) {
      setError(t('authDocs.errFileType', 'Please upload a PDF or image (JPG, PNG, HEIC).')); return
    }
    setError(null); setAuthReject(null); setAuthFieldError(null)
    setStage('analyzing')
    try {
      const url = await uploadToStorage(currentCase.id, f.name, f, f.type)
      if (!url) { setError(t('authDocs.errUploadFailed', 'Upload failed — please try again.')); setStage('error'); return }
      const res = await aiAPI.extractDocument(currentCase.id, url, f.name, 'auto')
      if (!res?.success) {
        setError(res?.error || t('authDocs.errUnreadable', 'We could not read this document. Please try a clearer PDF or photo.'))
        setPendingFile({ name: f.name, url, type: f.type, size: f.size })
        setDetectedType(null); setExtracted(null); setStage('error')
        return
      }
      // ★★ TEST 3.19 (patient push 3a): a referral only when the extractor says so. Anything that
      //   is neither (the API's coming 'other', a missing type) is filed as nothing at all; it was
      //   filed as a REFERRAL, with details read out of an unrelated visit summary.
      const dtype = classifyAuthUpload(res)
      setPendingFile({ name: f.name, url, type: f.type, size: f.size })
      setDetectedType(dtype)
      setExtracted(res)
      if (dtype === 'other') { setStage('not-usable'); return }
      if (dtype === 'referral') { setStage('review'); return }
      const expISO = normalizeExpiryClient(res.expirationDate)
      const visits = parseVisitsClient(res.visitsRemaining)
      if (expISO && expISO < todayISOClient()) { setAuthReject('expired'); setStage('reject'); return }
      if (visits !== null && visits <= 0) { setAuthReject('exhausted'); setStage('reject'); return }
      const conf = res.confidence || {}
      const numOk = (res.authorizationNumber || '').trim() && conf.auth_number === 'high'
      const expOk = !!expISO && conf.valid_through === 'high'
      setStage(numOk && expOk ? 'auth-valid' : 'auth-confirm')
    } catch (err) {
      console.error('[PatientUploadSection] Extract failed:', err)
      setError(err?.message || t('authDocs.errGeneric', 'Something went wrong reading the document.'))
      setStage('error')
    }
  }

  const handleSaveReferral = async () => {
    if (stage === 'saving' || !pendingFile) return
    setStage('saving'); setError(null)
    try {
      const entry = {
        id: `referral_p_${Date.now()}`,
        url: pendingFile.url, name: pendingFile.name,
        uploadedAt: new Date().toISOString(), size: pendingFile.size, type: pendingFile.type,
        uploadedBy: 'patient', uploadedByName: 'you', source: 'patient', isProcessed: !!extracted,
      }
      const existing = currentCase.adminDocuments || currentCase.documents || {}
      const prev = Array.isArray(existing.referral) ? existing.referral : []
      const updates = { adminDocuments: { ...existing, referral: [...prev, entry] } }
      if (extracted) {
        updates.extractedReferral = {
          referralDate: extracted.referralDate || '',
          referringDoctor: extracted.referringDoctor || '',
          reasonForConsultation: extracted.reasonForConsultation || '',
          referringDoctorPhone: extracted.referringDoctorPhone || '',
          referringDoctorFax: extracted.referringDoctorFax || '',
          validThrough: extracted.validThrough || '',
        }
      }
      await casesAPI.update(currentCase.id, updates)
      setStage('saved'); onUploaded?.(pendingFile.name)
      setTimeout(() => { resetFlow(); setExpanded(false) }, 1200)
    } catch (err) {
      console.error('[PatientUploadSection] Referral save failed:', err)
      setError(err?.message || t('authDocs.errSave', 'Could not save — please try again.'))
      setStage('review')
    }
  }

  const handleSaveAuthorization = async (values) => {
    const v = validateAuthClient(values)
    if (!v.ok) {
      if (v.code === 'no_number' || v.code === 'bad_expiry') { setAuthFieldError(v.code); return }
      setAuthReject(v.code); setStage('reject'); return
    }
    setAuthFieldError(null)
    setStage('saving')
    try {
      const res = await casesAPI.savePatientAuthorization(currentCase.id, {
        documentUrl: pendingFile.url, documentName: pendingFile.name,
        fileType: pendingFile.type, fileSize: pendingFile.size,
        // v235.5.0 — target the correct cassette (follow-up encounters write
        // straight to their encounters row; initial stays on appointment_request).
        // v242.170.0 — a prop still answers first (the caller may name the encounter
        // this upload is for); the case's own fallback goes through the owner.
        encounterNumber: encounterNumber ?? currentEncounterNumber(currentCase),
        authorizationNumber: values.authorizationNumber,
        expirationDate: values.expirationDate,
        visitsRemaining: values.visitsRemaining,
        authorizationDate: extracted?.authorizationDate || '',
        insuranceName: extracted?.insuranceName || '',
        insuranceId: extracted?.insuranceId || '',
        authorizedCodes: extracted?.authorizedCodes || '',
      })
      if (res?.success) {
        setStage('saved'); onUploaded?.(pendingFile.name)
        setTimeout(() => { resetFlow(); setExpanded(false) }, 1200)
      } else { setAuthFieldError('save'); setStage('auth-confirm') }
    } catch (err) {
      if (err?.status === 422 && err?.reasonCode) {
        if (err.reasonCode === 'no_number' || err.reasonCode === 'bad_expiry') {
          setAuthFieldError(err.reasonCode); setStage('auth-confirm')
        } else { setAuthReject(err.reasonCode); setStage('reject') }
      } else { setAuthFieldError('save'); setStage('auth-confirm') }
    }
  }

  if (!expanded && stage === 'idle') {
    return (
      <div className="space-y-2">
        {isHMO && !hasUploaded && (
          <div className="rounded-xl px-3.5 py-2.5 flex items-start gap-2" style={{ background: AMBER_BG, border: '1px solid #FDE68A' }}>
            <AlertTriangle size={14} style={{ color: AMBER, flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1">
              <p className="text-[13px] font-semibold mb-0.5" style={{ color: AMBER }}>{t('authDocs.hmoTitle', 'HMO referral needed')}</p>
              <p className="text-[12px]" style={{ color: '#92400E' }}>
                {t('authDocs.hmoBody', "Your insurance plan requires a referral letter from your primary care doctor for this visit. Upload it here so your physician's office has it ready when you arrive.")}
              </p>
            </div>
          </div>
        )}
        <button
          onClick={() => setExpanded(true)}
          className="w-full rounded-xl py-3 px-4 flex items-center justify-center gap-2 font-semibold text-[14px] transition-colors"
          style={{ background: ICE_BLUE, color: NAVY, border: `1.5px solid ${LIGHT_BLUE}` }}
        >
          <Plus size={16} style={{ color: ORELLA_BLUE }} />
          {hasUploaded ? t('authDocs.addAnother', 'Add another document') : t('authDocs.addDocument', 'Add a document')}
        </button>
        {bookingMode && onDefer && (
          <button onClick={onDefer} className="w-full text-center text-[12.5px] font-semibold pt-1" style={{ color: ORELLA_BLUE }}>
            {t('authDocs.deferLink', "I don't have it yet — I'll bring it to my visit")}
          </button>
        )}
      </div>
    )
  }

  if (stage === 'analyzing') {
    return (
      <div className="rounded-xl p-4" style={{ background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}` }}>
        <OrellaThinkingBubble
          message={t('authDocs.reading', 'Reading your document…')}
          subMessage={t('authDocs.readingSub', "Detecting whether it's an authorization or a referral, and pulling the key details.")}
        />
      </div>
    )
  }

  if ((stage === 'review' || (stage === 'saving' && detectedType === 'referral')) && extracted && detectedType === 'referral') {
    return (
      <ExtractedReviewCard
        detectedType="referral" data={extracted} fileName={pendingFile?.name}
        onSave={handleSaveReferral} onDiscard={resetFlow}
        saving={stage === 'saving'} error={error}
      />
    )
  }

  if ((stage === 'auth-confirm' || stage === 'auth-valid' || (stage === 'saving' && detectedType === 'authorization')) && extracted) {
    return (
      <AuthGateCard
        data={extracted} fileName={pendingFile?.name}
        requireConfirm={stage === 'auth-confirm' || stage === 'saving'}
        verified={stage === 'auth-valid'}
        saving={stage === 'saving'} fieldError={authFieldError}
        onSave={handleSaveAuthorization} onDiscard={resetFlow}
      />
    )
  }

  if (stage === 'reject') {
    return (
      <AuthRejectCard
        code={authReject} expiry={extracted?.expirationDate}
        onRetry={() => fileInputRef.current?.click()}
        onEnterManually={() => { setAuthFieldError(authReject); setStage('auth-confirm') }}
        onCancel={resetFlow}
      >
        <input ref={fileInputRef} type="file" accept="application/pdf,image/*,.heic,.heif" onChange={handleFilePick} className="hidden" />
      </AuthRejectCard>
    )
  }

  // TEST 3.19: not an authorization and not a referral — say so, file nothing, offer another file
  if (stage === 'not-usable') {
    return (
      <div className="rounded-xl p-4 space-y-3" data-auth-not-usable style={{ background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}` }}>
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} style={{ color: AMBER, flexShrink: 0, marginTop: 1 }} />
          <div>
            <p className="text-[13px] font-semibold" style={{ color: NAVY }}>{t('authDocs.notUsableTitle')}</p>
            <p className="text-[12px] mt-1" style={{ color: '#64748B' }}>{t('authDocs.notUsableBody')}</p>
          </div>
        </div>
        <button onClick={() => fileInputRef.current?.click()} className="w-full rounded-xl py-2.5 font-semibold text-[13px]" style={{ background: '#FFFFFF', color: NAVY, border: `1.5px solid ${LIGHT_BLUE}` }}>
          {t('authDocs.uploadDifferent')}
        </button>
        <button onClick={resetFlow} className="w-full text-center text-[12px] font-medium" style={{ color: '#64748B' }}>{t('authDocs.cancel', 'Cancel')}</button>
        <input ref={fileInputRef} type="file" accept="application/pdf,image/*,.heic,.heif" onChange={handleFilePick} className="hidden" />
      </div>
    )
  }

  if (stage === 'saved') {
    return (
      <div className="rounded-xl p-4 flex items-center gap-2" style={{ background: GREEN_LIGHT, border: '1px solid #BBF7D0' }}>
        <Check size={18} style={{ color: GREEN }} />
        <span className="text-[14px] font-semibold" style={{ color: GREEN }}>{t('authDocs.savedToCase', 'Saved to your case')}</span>
      </div>
    )
  }

  if (stage === 'error') {
    return (
      <div className="rounded-xl p-4 space-y-3" style={{ background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}` }}>
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} style={{ color: AMBER, flexShrink: 0, marginTop: 1 }} />
          <p className="text-[13px]" style={{ color: NAVY }}>{error || t('authDocs.somethingWrong', 'Something went wrong.')}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => fileInputRef.current?.click()} className="flex-1 rounded-xl py-2.5 font-semibold text-[13px]" style={{ background: '#FFFFFF', color: NAVY, border: `1.5px solid ${LIGHT_BLUE}` }}>
            {t('authDocs.tryAnother', 'Try another file')}
          </button>
        </div>
        <button onClick={resetFlow} className="w-full text-center text-[12px] font-medium" style={{ color: '#64748B' }}>{t('authDocs.cancel', 'Cancel')}</button>
        <input ref={fileInputRef} type="file" accept="application/pdf,image/*,.heic,.heif" onChange={handleFilePick} className="hidden" />
      </div>
    )
  }

  return (
    <div className="rounded-xl p-4 space-y-3" style={{ background: ICE_BLUE, border: `1.5px solid ${LIGHT_BLUE}` }}>
      <div className="flex items-center justify-between">
        <h4 className="text-[14px] font-bold" style={{ color: NAVY }}>{t('authDocs.addDocument', 'Add a document')}</h4>
        <button onClick={() => { setExpanded(false); setError(null) }} className="text-[12px] font-medium" style={{ color: '#64748B' }}>{t('authDocs.cancel', 'Cancel')}</button>
      </div>
      <button
        onClick={() => fileInputRef.current?.click()}
        className="w-full rounded-xl py-5 px-3 flex flex-col items-center justify-center gap-1.5 bg-white border-2 border-dashed transition-colors active:bg-blue-50"
        style={{ borderColor: LIGHT_BLUE, color: NAVY }}
      >
        <Upload size={18} style={{ color: ORELLA_BLUE }} />
        <span className="text-[13px] font-medium">{t('authDocs.tapToUpload', 'Tap to upload')}</span>
        <span className="text-[11px] text-slate-400">{t('authDocs.uploadHint', 'PDF or photo — Orella files it automatically')}</span>
      </button>
      {error && <p className="text-[12px] font-medium" style={{ color: '#DC2626' }}>{error}</p>}
      <input ref={fileInputRef} type="file" accept="application/pdf,image/*,.heic,.heif" onChange={handleFilePick} className="hidden" />
      {bookingMode && onDefer && (
        <button onClick={onDefer} className="w-full text-center text-[12.5px] font-semibold" style={{ color: ORELLA_BLUE }}>
          {t('authDocs.deferLink', "I don't have it yet — I'll bring it to my visit")}
        </button>
      )}
    </div>
  )
}

function normalizeExpiryClient(raw) {
  if (!raw) return null
  const s = String(raw).trim()
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) return s
  m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2}|\d{4})$/)
  if (m) {
    const mm = +m[1], dd = +m[2], yy = m[3]
    const year = +yy + (yy.length === 2 ? 2000 : 0)
    const d = new Date(year, mm - 1, dd)
    if (d.getFullYear() === year && d.getMonth() === mm - 1 && d.getDate() === dd) {
      return `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
    }
    return null
  }
  const d = new Date(s)
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  return null
}
function parseVisitsClient(raw) {
  if (raw === null || raw === undefined || String(raw).trim() === '') return null
  const m = String(raw).match(/-?\d+/)
  return m ? parseInt(m[0], 10) : null
}
function todayISOClient() {
  const t = new Date()
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
}
function validateAuthClient({ authorizationNumber, expirationDate, visitsRemaining }) {
  const num = (authorizationNumber || '').trim()
  if (!num) return { ok: false, code: 'no_number' }
  const iso = normalizeExpiryClient(expirationDate)
  if (!iso) return { ok: false, code: 'bad_expiry' }
  if (iso < todayISOClient()) return { ok: false, code: 'expired' }
  const v = parseVisitsClient(visitsRemaining)
  if (v !== null && v <= 0) return { ok: false, code: 'exhausted' }
  return { ok: true, expiryISO: iso, visits: v }
}

function ExtractedDetailRow({ icon: Icon, label, value, danger = false }) {
  if (!value) return null
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b last:border-b-0" style={{ borderColor: '#F1F5F9' }}>
      <span className="flex items-center gap-1.5 text-[12px] flex-shrink-0" style={{ color: '#64748B' }}>
        {Icon && <Icon size={13} style={{ color: '#94A3B8' }} />}{label}
      </span>
      <span className="text-[12px] font-semibold text-right" style={{ color: danger ? AMBER : NAVY }}>{value}</span>
    </div>
  )
}

function ExtractedReviewCard({ detectedType, data, fileName, onSave, onDiscard, saving, error }) {
  const { t } = useI18n()
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full tracking-wide" style={{ background: GREEN_LIGHT, color: '#15803D' }}>{t('authDocs.referralDetected', 'REFERRAL DETECTED')}</span>
        <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: GREEN_LIGHT, color: GREEN }}>{t('authDocs.processed', 'Processed')}</span>
      </div>
      <div className="bg-white rounded-xl border" style={{ borderColor: '#E5E9EE' }}>
        <div className="flex items-center gap-2.5 p-3 border-b" style={{ borderColor: '#F1F5F9' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: ICE_BLUE }}>
            <FileText size={16} style={{ color: ORELLA_BLUE }} />
          </div>
          <span className="text-[12.5px] font-bold truncate" style={{ color: NAVY }}>{fileName || t('authDocs.document', 'Document')}</span>
        </div>
        <div className="p-3">
          <p className="flex items-center gap-1.5 text-[12px] font-extrabold mb-1.5" style={{ color: NAVY }}>
            <ClipboardList size={14} style={{ color: ORELLA_BLUE }} />{t('authDocs.referralDetails', 'Referral Details')}
          </p>
          <ExtractedDetailRow icon={Calendar} label={t('authDocs.dateOfReferral', 'Date of Referral')} value={data.referralDate} />
          <ExtractedDetailRow icon={User} label={t('authDocs.fromDoctor', 'From Doctor')} value={data.referringDoctor} />
          <ExtractedDetailRow icon={Stethoscope} label={t('authDocs.reasonForConsultation', 'Reason for Consultation')} value={data.reasonForConsultation} />
          <ExtractedDetailRow icon={Phone} label={t('authDocs.phone', 'Phone')} value={data.referringDoctorPhone} />
          <ExtractedDetailRow icon={Printer} label={t('authDocs.fax', 'Fax')} value={data.referringDoctorFax} />
        </div>
      </div>
      {error && <p className="text-[12px] font-medium" style={{ color: '#DC2626' }}>{error}</p>}
      <div className="flex gap-2">
        <button onClick={onSave} disabled={saving} className="flex-1 rounded-xl py-3 font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: NAVY, color: '#FFFFFF' }}>
          {saving && <OrellaSpinner size={16} tone="inherit" />}{saving ? t('authDocs.saving', 'Saving…') : t('authDocs.saveToCase', 'Save to my case')}
        </button>
        <button onClick={onDiscard} disabled={saving} className="rounded-xl py-3 px-4 font-semibold text-[14px] disabled:opacity-60" style={{ background: '#FFFFFF', color: '#64748B', border: '1.5px solid #E2E8F0' }}>{t('authDocs.discard', 'Discard')}</button>
      </div>
    </div>
  )
}

function AuthGateCard({ data, fileName, requireConfirm, verified, saving, fieldError, onSave, onDiscard }) {
  const { t } = useI18n()
  const [num, setNum] = useState(data.authorizationNumber || '')
  const [exp, setExp] = useState(data.expirationDate || '')
  const [visits, setVisits] = useState(data.visitsRemaining === 0 || data.visitsRemaining ? String(data.visitsRemaining) : '')

  const errMsg = fieldError === 'no_number' ? t('authDocs.errNoNumber', 'An authorization number is required.')
    : fieldError === 'bad_expiry' ? t('authDocs.errBadExpiry', 'Enter a valid expiration date (MM/DD/YYYY).')
    : fieldError === 'save' ? t('authDocs.errSave', 'Could not save — please try again.')
    : null

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full tracking-wide" style={{ background: '#E0EDFF', color: '#1D4ED8' }}>{t('authDocs.authDetected', 'AUTHORIZATION DETECTED')}</span>
        {verified
          ? <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: GREEN_LIGHT, color: GREEN }}>{t('authDocs.verified', 'Verified')}</span>
          : <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: AMBER_BG, color: AMBER }}>{t('authDocs.pleaseVerify', 'Please verify')}</span>}
      </div>

      <div className="bg-white rounded-xl border" style={{ borderColor: '#E5E9EE' }}>
        <div className="flex items-center gap-2.5 p-3 border-b" style={{ borderColor: '#F1F5F9' }}>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: ICE_BLUE }}>
            <FileText size={16} style={{ color: ORELLA_BLUE }} />
          </div>
          <span className="text-[12.5px] font-bold truncate" style={{ color: NAVY }}>{fileName || t('authDocs.authorization', 'Authorization')}</span>
        </div>
        <div className="p-3 space-y-3">
          <p className="flex items-center gap-1.5 text-[12px] font-extrabold" style={{ color: NAVY }}>
            <ShieldCheck size={14} style={{ color: ORELLA_BLUE }} />
            {verified ? t('authDocs.authDetails', 'Authorization Details') : t('authDocs.confirmCritical', 'Confirm these — critical for claims')}
          </p>

          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wide block mb-1" style={{ color: '#64748B' }}>{t('authDocs.authNumber', 'Authorization Number')}</label>
            <input value={num} onChange={(e) => setNum(e.target.value)} placeholder={t('authDocs.authNumberPlaceholder', 'e.g. ABD014')}
              className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
              style={{ border: `1.5px solid ${requireConfirm ? '#FCD34D' : '#E2E8F0'}`, background: requireConfirm ? '#FFFBEB' : '#FFFFFF', color: NAVY }} />
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wide block mb-1" style={{ color: '#64748B' }}>{t('authDocs.expirationDate', 'Expiration Date')}</label>
            <input value={exp} onChange={(e) => setExp(e.target.value)} placeholder={t('authDocs.datePlaceholder', 'MM/DD/YYYY')}
              className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
              style={{ border: `1.5px solid ${requireConfirm ? '#FCD34D' : '#E2E8F0'}`, background: requireConfirm ? '#FFFBEB' : '#FFFFFF', color: NAVY }} />
          </div>
          <div>
            <label className="text-[10.5px] font-bold uppercase tracking-wide block mb-1" style={{ color: '#64748B' }}>
              {t('authDocs.visitsRemaining', 'Visits Remaining')} <span className="font-normal lowercase tracking-normal text-slate-400">{t('authDocs.visitsHint', '(blank if not on the form)')}</span>
            </label>
            <input value={visits} onChange={(e) => setVisits(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="—"
              className="w-full rounded-lg px-3 py-2 text-[13px] focus:outline-none"
              style={{ border: '1.5px solid #E2E8F0', background: '#FFFFFF', color: NAVY }} />
          </div>

          {(data.insuranceName || data.authorizationDate || data.authorizedCodes) && (
            <div className="pt-1">
              <ExtractedDetailRow icon={Calendar} label={t('authDocs.authDate', 'Authorization Date')} value={data.authorizationDate} />
              <ExtractedDetailRow icon={Building2} label={t('authDocs.insurance', 'Insurance')} value={data.insuranceName} />
              <ExtractedDetailRow icon={Hash} label={t('authDocs.insuranceId', 'Insurance ID')} value={data.insuranceId} />
              <ExtractedDetailRow icon={FileText} label={t('authDocs.authorizedCodes', 'Authorized Codes')} value={data.authorizedCodes} />
            </div>
          )}
        </div>
      </div>

      {errMsg && <p className="text-[12px] font-medium" style={{ color: AMBER }}>{errMsg}</p>}

      <div className="flex gap-2">
        <button
          onClick={() => onSave({ authorizationNumber: num, expirationDate: exp, visitsRemaining: visits })}
          disabled={saving}
          className="flex-1 rounded-xl py-3 font-semibold text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
          style={{ background: NAVY, color: '#FFFFFF' }}
        >
          {saving && <OrellaSpinner size={16} tone="inherit" />}{saving ? t('authDocs.saving', 'Saving…') : (verified ? t('authDocs.saveToCase', 'Save to my case') : t('authDocs.confirmSave', 'Confirm & save'))}
        </button>
        <button onClick={onDiscard} disabled={saving} className="rounded-xl py-3 px-4 font-semibold text-[14px] disabled:opacity-60" style={{ background: '#FFFFFF', color: '#64748B', border: '1.5px solid #E2E8F0' }}>{t('authDocs.discard', 'Discard')}</button>
      </div>
    </div>
  )
}

function AuthRejectCard({ code, expiry, onRetry, onEnterManually, onCancel, children }) {
  const { t } = useI18n()
  const COPY = {
    expired: { title: t('authDocs.rejExpiredTitle', 'This authorization has expired'), body: t('authDocs.rejExpiredBody', "It expired on {{date}}. An expired authorization can't be used for billing — your physician's office will need a current one. Nothing was saved.", { date: expiry || t('authDocs.theListedDate', 'the listed date') }) },
    exhausted: { title: t('authDocs.rejExhaustedTitle', 'This authorization is used up'), body: t('authDocs.rejExhaustedBody', "The form shows 0 visits remaining, so it can't cover this visit. A new authorization is needed. Nothing was saved.") },
    no_number: { title: t('authDocs.rejNoNumberTitle', 'No authorization number found'), body: t('authDocs.rejNoNumberBody', "We couldn't read an authorization number. Re-upload a clearer copy, or enter the number from the form yourself.") },
    bad_expiry: { title: t('authDocs.rejBadExpiryTitle', "Couldn't read the expiration date"), body: t('authDocs.rejBadExpiryBody', 'Enter the expiration date from the form so we can confirm the authorization is current.') },
  }
  const c = COPY[code] || COPY.no_number
  const canManual = code === 'no_number' || code === 'bad_expiry'
  const tag = code === 'expired' ? t('authDocs.tagExpired', 'Expired') : code === 'exhausted' ? t('authDocs.tagNoVisits', 'No visits left') : t('authDocs.tagNeedsAttention', 'Needs attention')
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full tracking-wide" style={{ background: '#E0EDFF', color: '#1D4ED8' }}>{t('authDocs.authDetected', 'AUTHORIZATION DETECTED')}</span>
        <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: AMBER_BG, color: AMBER }}>{tag}</span>
      </div>
      <div className="rounded-xl p-4 text-center" style={{ background: '#FFFCF5', border: '1.5px solid #F4E3C1' }}>
        <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto mb-2.5" style={{ background: ICE_BLUE, border: '2px solid #F4E3C1' }}>
          <AlertTriangle size={20} style={{ color: AMBER }} />
        </div>
        <p className="text-[14px] font-extrabold" style={{ color: NAVY }}>{c.title}</p>
        <p className="text-[12px] mt-1.5" style={{ color: '#5B6B7B', lineHeight: 1.5 }}>{c.body}</p>
      </div>
      <div className="flex gap-2">
        {canManual ? (
          <button onClick={onEnterManually} className="flex-1 rounded-xl py-3 font-semibold text-[14px]" style={{ background: NAVY, color: '#FFFFFF' }}>{t('authDocs.enterManually', 'Enter it manually')}</button>
        ) : (
          <button onClick={onRetry} className="flex-1 rounded-xl py-3 font-semibold text-[14px]" style={{ background: '#FFFFFF', color: NAVY, border: `1.5px solid ${LIGHT_BLUE}` }}>{t('authDocs.tryAnother', 'Try another file')}</button>
        )}
        <button onClick={onCancel} className="rounded-xl py-3 px-4 font-semibold text-[14px]" style={{ background: '#FFFFFF', color: '#64748B', border: '1.5px solid #E2E8F0' }}>{t('authDocs.cancel', 'Cancel')}</button>
      </div>
      {children}
    </div>
  )
}

/**
 * Categorize a single document entry as belonging in:
 *   'forThisVisit'  — pre-visit support docs (patient OR MD uploaded)
 *   'fromThisVisit' — Orella-generated artifacts from this visit
 *
 * v217.0.0: 3-class uploader model. Resolution order:
 *   1. Canonical `uploadedBy: 'patient' | 'md' | 'orella'`
 *   2. Legacy `source` field:
 *        - 'patient'      → forThisVisit (patient upload)
 *        - 'md_generated' → fromThisVisit (the legacy field was only ever used
 *                            for system-generated artifacts; MD uploads pre-
 *                            v217 were untagged and fall to the heuristic)
 *   3. Heuristic fallback: Orella-generated artifacts carry `signedAt`,
 *      `config`, `procedures`, or `diagnosis`. Raw uploads carry `url` +
 *      `uploadedAt` with no system signals → forThisVisit.
 *   4. Genuinely ambiguous legacy entries → fromThisVisit (safe default).
 */
function getEntryDestination(entry) {
  // 1. Canonical flag
  if (entry?.uploadedBy === 'patient') return 'forThisVisit'
  if (entry?.uploadedBy === 'md')      return 'forThisVisit'
  if (entry?.uploadedBy === 'orella')  return 'fromThisVisit'

  // 2. Legacy `source` field
  if (entry?.source === 'patient')      return 'forThisVisit'
  if (entry?.source === 'md_generated') return 'fromThisVisit'

  // 3. Heuristic: Orella-generated signals
  if (entry?.signedAt) return 'fromThisVisit'
  if (entry?.config && Object.keys(entry.config).length > 0) return 'fromThisVisit'
  if (entry?.procedures || entry?.diagnosis) return 'fromThisVisit'

  // Raw upload signature (url + uploadedAt, no system signals) → forThisVisit
  if (entry?.url && entry?.uploadedAt) return 'forThisVisit'

  // 4. Safe default
  return 'fromThisVisit'
}

export default function AuthorizationsScreen() {
  const navigate = useNavigate()
  const { currentCase, loadCases } = useCaseStore()
  const { profile } = useProfileStore()
  const { t } = useI18n()

  const [selectedDocument, setSelectedDocument] = useState(null)
  // v225.30.0: filename of an upload currently being saved + refetched, so the
  // FOR THIS VISIT list shows an optimistic "Saving…" card during the gap
  // between the save completing and loadCases() repopulating the real card.
  const [populatingDoc, setPopulatingDoc] = useState(null)

  // Pull entries from all known locations and split by source.
  const { forThisVisit, fromThisVisit } = useMemo(() => {
    if (!currentCase) {
      return {
        forThisVisit: { auths: [], refs: [] },
        fromThisVisit: { auths: [], refs: [] },
      }
    }

    const adminDocs = currentCase.adminDocuments || currentCase.documents || {}
    const followUp = currentCase.followUp || {}

    const docAuths = Array.isArray(adminDocs.authorization) ? adminDocs.authorization
                     : Array.isArray(adminDocs.authorizations) ? adminDocs.authorizations : []
    const docRefs = Array.isArray(adminDocs.referral) ? adminDocs.referral
                    : Array.isArray(adminDocs.referrals) ? adminDocs.referrals : []
    const followUpAuths = followUp.authorizations || followUp.authorization || []
    const followUpRefs = followUp.referrals || followUp.referral || []
    const generatedAuths = followUp.generatedAuthorizations || []
    const generatedRefs = followUp.generatedReferrals || []

    // v217.0.0: followUp.* and follow-up generated arrays are ALWAYS Orella-
    // generated artifacts (signed packets, delivered referral letters). Tag
    // them with canonical uploadedBy + legacy source for back-compat readers.
    const tagOrella = (e) => ({ ...e, uploadedBy: e?.uploadedBy || 'orella', source: e?.source || 'md_generated' })
    const taggedFollowUpAuths = (Array.isArray(followUpAuths) ? followUpAuths : []).map(tagOrella)
    const taggedFollowUpRefs  = (Array.isArray(followUpRefs)  ? followUpRefs  : []).map(tagOrella)
    const taggedGenAuths      = (Array.isArray(generatedAuths) ? generatedAuths : []).map(tagOrella)
    const taggedGenRefs       = (Array.isArray(generatedRefs)  ? generatedRefs  : []).map(tagOrella)

    const allAuths = [...docAuths, ...taggedFollowUpAuths, ...taggedGenAuths]
    const allRefs  = [...docRefs,  ...taggedFollowUpRefs,  ...taggedGenRefs]

    // Deduplicate by id
    const dedup = (arr) => arr.filter((x, i, a) => !x?.id || a.findIndex(y => y?.id === x?.id) === i)
    const uniqueAuths = dedup(allAuths).map(a => ({ ...a, type: 'authorization' }))
    const uniqueRefs  = dedup(allRefs).map(r => ({ ...r, type: 'referral' }))

    // Split by destination (forThisVisit vs fromThisVisit)
    const forVisit = { auths: [], refs: [] }
    const fromVisit = { auths: [], refs: [] }
    for (const a of uniqueAuths) {
      (getEntryDestination(a) === 'forThisVisit' ? forVisit.auths : fromVisit.auths).push(a)
    }
    for (const r of uniqueRefs) {
      (getEntryDestination(r) === 'forThisVisit' ? forVisit.refs : fromVisit.refs).push(r)
    }
    return { forThisVisit: forVisit, fromThisVisit: fromVisit }
  }, [currentCase])

  // TEST 2.22: the store carries the projection (priorAuthRequests); a case object that came
  // with the raw caseRequests is read the same way.
  const payerAuths = useMemo(() => {
    if (!currentCase) return []
    return Array.isArray(currentCase.priorAuthRequests) ? currentCase.priorAuthRequests : priorAuthPayerEntries(currentCase.caseRequests)
  }, [currentCase])

  const isHMO = isHMOInsurance(currentCase?.profile?.insurance || profile?.insurance)
  const forCount = forThisVisit.auths.length + forThisVisit.refs.length
  const fromCount = fromThisVisit.auths.length + fromThisVisit.refs.length + payerAuths.length

  const handleUploaded = async (fileName) => {
    // Show an optimistic "Saving…" card immediately, then refresh so the real
    // entry replaces it. Cleared in finally once the refetch completes.
    if (fileName) setPopulatingDoc(fileName)
    try { await loadCases?.() } catch (e) { /* non-fatal */ }
    finally { setPopulatingDoc(null) }
  }

  if (!currentCase) {
    return (
      <div className="min-h-full flex flex-col bg-slate-100">
        <header className="bg-white border-b border-slate-200 px-4 pt-12 pb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/case/home')}
              className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"
            >
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <h1 className="text-lg font-bold" style={{ color: NAVY }}>
              {t('authDocs.title', 'Authorizations & Referrals')}
            </h1>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-slate-500">{t('common.loadingEllipsis', 'Loading…')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full flex flex-col bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 pt-12 pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/case/home')}
            className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"
          >
            <ArrowLeft size={18} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold" style={{ color: NAVY }}>
              {t('authDocs.title', 'Authorizations & Referrals')}
            </h1>
            <p className="text-xs text-slate-500">
              {t('authDocs.forAndFromThisVisit', 'For this visit, and from this visit')}
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* ─── For this visit (patient-uploaded) ─── */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {t('authDocs.forThisVisit', 'For this visit')}
            </p>
            {(forCount > 0 || populatingDoc) && (
              <span className="text-[11px] text-slate-400">
                {(() => { const n = forCount + (populatingDoc ? 1 : 0); return n === 1 ? '1 document' : `${n} documents` })()}
              </span>
            )}
          </div>

          <PatientUploadSection
            currentCase={currentCase}
            isHMO={isHMO}
            hasUploaded={forCount > 0}
            onUploaded={handleUploaded}
          />

          {(forCount > 0 || populatingDoc) && (
            <div className="space-y-2.5 mt-3">
              {/* v225.30.0: optimistic "Saving…" card — bridges the gap between
                  the upload saving and loadCases() repopulating the real card. */}
              {populatingDoc && (
                <>
                  <style>{`@keyframes authSavePulse{0%,100%{opacity:1}50%{opacity:.62}}`}</style>
                  <div className="w-full bg-white rounded-xl p-4 border" style={{ borderColor: '#DCEEFF', boxShadow: '0 1px 3px rgba(70,168,249,0.08)', animation: 'authSavePulse 1.8s ease-in-out infinite' }}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ICE_BLUE }}>
                        <OrellaSpinner size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm mb-1 truncate" style={{ color: NAVY }}>{populatingDoc}</h4>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: ICE_BLUE, color: ORELLA_BLUE }}>{t('security.saving', 'Saving…')}</span>
                          <span className="text-xs text-slate-400">{t('authDocs.addingToYourCase', 'Adding to your case')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {forThisVisit.refs.map((ref, idx) => (
                <DocumentCard
                  key={`for-ref-${ref.id || idx}`}
                  document={ref}
                  type="referral"
                  color={ORELLA_BLUE}
                  lightColor={ICE_BLUE}
                  onClick={() => setSelectedDocument(ref)}
                />
              ))}
              {forThisVisit.auths.map((auth, idx) => (
                <DocumentCard
                  key={`for-auth-${auth.id || idx}`}
                  document={auth}
                  type="authorization"
                  color={GREEN}
                  lightColor={GREEN_LIGHT}
                  onClick={() => setSelectedDocument(auth)}
                />
              ))}
            </div>
          )}
        </section>

        {/* ─── From this visit (MD-generated) ─── */}
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {t('authDocs.fromThisVisit', 'From this visit')}
            </p>
            {fromCount > 0 && (
              <span className="text-[11px] text-slate-400">
                {fromCount === 1 ? '1 from Orella' : `${fromCount} from Orella`}
              </span>
            )}
          </div>

          {fromCount > 0 ? (
            <div className="space-y-2.5">
              {payerAuths.map((entry, idx) => (
                <PayerStatusCard key={`payer-${entry.id || idx}`} entry={entry} />
              ))}
              {fromThisVisit.auths.map((auth, idx) => (
                <DocumentCard
                  key={`from-auth-${auth.id || idx}`}
                  document={auth}
                  type="authorization"
                  color={GREEN}
                  lightColor={GREEN_LIGHT}
                  onClick={() => setSelectedDocument(auth)}
                />
              ))}
              {fromThisVisit.refs.map((ref, idx) => (
                <DocumentCard
                  key={`from-ref-${ref.id || idx}`}
                  document={ref}
                  type="referral"
                  color={ORELLA_BLUE}
                  lightColor={ICE_BLUE}
                  onClick={() => setSelectedDocument(ref)}
                />
              ))}
            </div>
          ) : (
            <div
              className="rounded-xl bg-white border border-slate-200 p-5 text-center"
            >
              <div className="w-10 h-10 rounded-full mx-auto mb-2 flex items-center justify-center" style={{ background: ICE_BLUE }}>
                <Sparkles size={16} style={{ color: ORELLA_BLUE }} />
              </div>
              <p className="text-[13px] font-semibold" style={{ color: NAVY }}>
                {t('authDocs.emptyTitle', 'Nothing here yet')}
              </p>
              <p className="text-[12px] text-slate-500 mt-1">
                {t('authDocs.emptyBody', 'Authorization requests and referrals generated by Orella during your visit will appear here.')}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* Document Detail Modal */}
      <DocumentDetailModal
        document={selectedDocument}
        onClose={() => setSelectedDocument(null)}
        caseId={currentCase?.id}
      />
    </div>
  )
}
